import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { EstadoErro } from '@/components/patterns/states';
import { setAuthPort } from '@/data/auth';
import { iniciarKeycloak } from '@/data/auth/keycloak';

/**
 * Portão de entrada do modo `rest`.
 *
 * Só autentica no Keycloak — não há mais uma etapa separada de "descobrir os
 * acessos" aqui. `GET /instalacoes` já é o hub inteiro (todas as instalações
 * que o usuário acessa, resolvido no servidor a partir do e-mail do token), e
 * cada tela trata "nenhuma instalação no escopo" com o próprio estado vazio —
 * não precisa de um passo de boot dedicado pra isso.
 */

type Etapa = 'autenticando' | 'pronto' | 'erro';

export function BootGate({ children }: { children: React.ReactNode }) {
  const [etapa, setEtapa] = useState<Etapa>('autenticando');
  const [erro, setErro] = useState<Error | null>(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;

    async function bootar() {
      try {
        setErro(null);
        setEtapa('autenticando');

        // `iniciarKeycloak` é single-flight: a segunda montagem do StrictMode
        // espera a MESMA Promise em vez de repetir o `init()` ou seguir sem
        // identificador.
        const sessao = await iniciarKeycloak();
        if (!ativo) return;
        setAuthPort(sessao.port);
        setEtapa('pronto');
      } catch (causa) {
        if (!ativo || (causa instanceof DOMException && causa.name === 'AbortError')) return;
        setErro(causa instanceof Error ? causa : new Error(String(causa)));
        setEtapa('erro');
      }
    }

    void bootar();
    return () => {
      ativo = false;
    };
  }, [tentativa]);

  const tentarDeNovo = useCallback(() => setTentativa((valor) => valor + 1), []);

  if (etapa === 'pronto') return <>{children}</>;

  return (
    <div className="flex min-h-full items-center justify-center bg-bg-page p-6">
      <div className="w-full max-w-lg rounded-card border border-border bg-bg-surface shadow-sm">
        {etapa === 'erro' ? (
          <EstadoErro erro={erro} aoTentarDeNovo={tentarDeNovo} />
        ) : (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center" role="status">
            <Loader2 className="size-6 animate-spin text-primary" aria-hidden="true" />
            <p className="text-card-title">Preparando o Portal de Atualizações</p>
            <p className="text-body text-on-surface-muted">Autenticando no Keycloak…</p>
          </div>
        )}
      </div>
    </div>
  );
}
